import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from "@angular/core";
import {DatePipe} from "@angular/common";
import {plainToClass} from "class-transformer";
import {SelectItem} from "primeng/api";
import {forkJoin} from "rxjs";

import {UserService} from '@app/core';
import {CommonTransformationService} from "@app/shared/services/common-transformation.service";
import {ChargeCodeService} from "@app/shared/services/charge-code.service";
import {DateService} from '@app/shared/services/date.service';
import {TimesheetWorkLocationType} from "@app/shared/classes/common";
import {PrimeNgConfig} from "@app/shared/classes/prime-ng-config";

import {
  ChargeCode,
  ChargeCodeProperty,
  ChargeCodeType,
  LaborCategoryInfo,
  LaborCategoryProperty,
  ProjectChargeCode,
  PurchaseChargeCode
} from "@app/shared/classes/charge-code";

import {Format} from '@app/shared/enums/format.enum';
import {Transaction} from '@app/shared/enums/txn-type.enum';
import {PayCodeType, ProjectService, ProjectWorkLocation, WorkLocationType} from "@app/shared";

@Component({
  selector: "app-my-charge-code-dropdown",
  templateUrl: "./my-charge-code-dropdown.component.html",
  styleUrls: ["./my-charge-code-dropdown.component.css"],
  providers: [ChargeCodeService]
})

export class MyChargeCodeDropdownComponent implements OnInit, OnChanges {

  PrimeNgConfig = PrimeNgConfig;

  @Input() chargeCodeId: number;                                              // input (already selected) charge code id
  @Input() chargeCode: ProjectChargeCode;                                     // charge code object to be updated
  @Input() txnType: Transaction;                                              // txnType for charge code
  @Input() authorizedFrom: string;                                            // authorized charge code from date in local time zone
  @Input() authorizedThru: string;                                            // authorized charge code to date in local time zone
  @Input() chargeNumber: number;                                              // optional charge code number for display
  @Input() chargeCodeDisplayProperties: ChargeCodeProperty[];                 // charge code properties to display in the dropdown menu
  @Input() userId: number;                                                    // optional userId for whom the charge codes have to be fetched

  @Input() laborCategoryId: number;                                           // previously selected labor category id
  @Input() laborCategoryCode: string;                                         // previously selected labor category code for display
  @Input() laborCategoryDisplayProperties: LaborCategoryProperty[];           // labor category properties to display in the dropdown menu

  @Input() clientLocationId: number;
  @Input() userResidenceId: number;
  @Input() officeLocationId: number;
  @Input() chargeCodesToExclude: Array<ProjectChargeCode | PurchaseChargeCode>; // charge code to exclude from the dropdown menu
  @Input() chargeCodeTypesToInclude: ChargeCodeType[];

  @Output() handler: EventEmitter<ProjectChargeCode> = new EventEmitter();    // user selected charge code for client to use

  selectedChargeCode: ProjectChargeCode;                                      // user selected charge code
  chargeCodeDropdown: SelectItem[];                                           // charge code dropdown menu
  chargeCodeFound: boolean = true;
  chargeCodeErrorMessage: string = " is expired or inactive.";

  workLocationDropdown: SelectItem[];

  selectedLaborCategory: LaborCategoryInfo;                                   // user selected labor category
  laborCategoryDropdown: SelectItem[];                                        // labor category dropdown menu for the selected charge code
  laborCategoryFound: boolean = true;
  laborCategoryErrorMessage: string = " is expired or inactive.";

  workLocationFound: boolean = true;

  selectedWorkLocation: any;                                                  //it can be either userAddress or orgAddress or WorkLocation
  projectWorkLocations: ProjectWorkLocation[];                           // Used to populate work location drop down for Indirect RT charge codes.

  private defaultLaborCategoryProperties = [
    LaborCategoryProperty.LaborCategoryCode
  ];

  loading: boolean = false;

  constructor(private datePipe: DatePipe, private user: UserService, private transform: CommonTransformationService, private dateService: DateService, private ccDataService: ChargeCodeService, private projectService: ProjectService) {
  }

  ngOnInit() {
  }

  /**
   * @desc build charge code dropdown menu and set it if the input charge code is provided
   */
  ngOnChanges(changes: SimpleChanges): void {

    if (!this.txnType) {
      console.error("txnType must be provided! cannot initialize my-charge-code-dropdown");
      return;
    }

    // set default display properties
    if (!this.chargeCodeDisplayProperties) {
      this.chargeCodeDisplayProperties = this.ccDataService.DefaultChargeCodeProperties;
    }
    if (!this.laborCategoryDisplayProperties) {
      this.laborCategoryDisplayProperties = this.defaultLaborCategoryProperties;
    }

    if (!this.authorizedFrom) {
      this.authorizedFrom = this.datePipe.transform(new Date(), Format.DataserviceDate);
    }

    let chargeCodeId: number;
    if (this.chargeCode && this.chargeCode.id) {
      chargeCodeId = this.chargeCode.id;
    } else if (this.chargeCodeId) {
      chargeCodeId = this.chargeCodeId;
    }

    let userId: number;
    if (this.userId) {
      userId = this.userId;
    } else {
      userId = this.user.info.userId;
    }

    // prevent circular loop of emitting already selected chargeCodeId and laborCategoryId combination
    if (chargeCodeId && this.selectedChargeCode && chargeCodeId == this.selectedChargeCode.id) {
      if (this.laborCategoryId && this.selectedLaborCategory && this.laborCategoryId == this.selectedLaborCategory.laborCategoryId
        && ((this.clientLocationId && this.selectedWorkLocation && this.clientLocationId == this.selectedWorkLocation.clientLocationId)
          || (this.userResidenceId && this.selectedWorkLocation && this.userResidenceId == this.selectedWorkLocation.id)
          || (this.officeLocationId && this.selectedWorkLocation
            && (this.selectedChargeCode.codeType == ChargeCodeType.DirectProject || this.selectedChargeCode.codeType == ChargeCodeType.IndirectProject)
            && this.officeLocationId == this.selectedWorkLocation.officeLocationId)
          || ((this.officeLocationId && this.selectedWorkLocation
            && (this.selectedChargeCode.codeType == ChargeCodeType.Indirect || this.selectedChargeCode.codeType == ChargeCodeType.Unallowable)
            && this.officeLocationId == this.selectedWorkLocation.id))
        )) {
        // input chargeCodeId and laborCategoryId are already selected
        console.log("returning because chargeCode, laborCategory and worklocation are previously selected...");
        return;
      }
      if (!this.laborCategoryId && !this.selectedLaborCategory) {
        // handle chargeCode without laborCategory
        console.log("returning because chargeCode is previously selected and laborCategory is null...");
        return;
      }
    }

    // load charge code from the cache
    let chargeCodeRange = this.dateService.toRange(this.authorizedFrom, this.authorizedThru);
    const chargeCodeIndex: string = this.ccDataService.getIndexForChargeCode(this.txnType, chargeCodeRange, userId);
    const workLocationIndex: string = this.ccDataService.getIndexForChargeCode(this.txnType, this.ccDataService.ProjectWorkLocationCacheIndexIdentifier, userId);
    this.chargeCodeDropdown = this.user.getAuthorizedChargeCodeArray(chargeCodeIndex);
    this.projectWorkLocations = this.user.getProjectWorkLocation(workLocationIndex);

    if (!this.chargeCodeDropdown  || !this.projectWorkLocations) {
      this.getChargeCodeAndWorkLocationsDropdown(this.txnType, this.authorizedFrom, this.authorizedThru, this.chargeCodesToExclude, this.chargeCodeTypesToInclude, chargeCodeId, userId);
    } else if (chargeCodeId) {
      this.handleChargeCodeAndLaborCategory(chargeCodeId, this.txnType, this.chargeCodesToExclude, this.chargeCodeTypesToInclude);
    } else {
      this.handleChargeCodesToExclude(this.chargeCodesToExclude);
    }
  }

  /**
   * @desc retrieve charge code from either cache or backend
   */
  getChargeCodeAndWorkLocationsDropdown(txnType: Transaction, authorizedFrom: string, authorizedThru: string,
                                        chargeCodesToExclude: Array<ProjectChargeCode | PurchaseChargeCode>, chargeCodeTypesToInclude: ChargeCodeType[],
                                        chargeCodeId?: number, userId?: number) {

    // Get from cache only if the logged-in user is accessing his/her own charge codes.
    // In case of delegation
    if (this.chargeCodeDropdown && (this.userId && this.userId == this.user.info.userId)) {
      this.handleChargeCodeAndLaborCategory(chargeCodeId, txnType, chargeCodesToExclude, chargeCodeTypesToInclude);
    } else if (txnType && authorizedFrom) {
      this.loading = true;
      if (this.userId && this.userId == this.user.info.userId) {
        forkJoin(
          this.ccDataService.getMyChargeCodeAsDropdown(txnType, authorizedFrom, authorizedThru, this.chargeCodeDisplayProperties),
          this.projectService.getProjectOnsiteWorkLocations()
        ).subscribe(res => {
          this.chargeCodeDropdown = res[0];
          let chargeCodeRange = this.dateService.toRange(authorizedFrom,authorizedThru);
          const chargeCodeIndex: string = this.ccDataService.getIndexForChargeCode(txnType, chargeCodeRange, this.userId);
          const workLocationIndex: string = this.ccDataService.getIndexForChargeCode(txnType, this.ccDataService.ProjectWorkLocationCacheIndexIdentifier, this.userId);
          this.user.saveAuthorizedChargeCodeArray(chargeCodeIndex, res[0]);
          this.projectWorkLocations = plainToClass(ProjectWorkLocation, res[1].list);
          this.user.saveProjectWorkLocations(workLocationIndex, this.projectWorkLocations);
          this.handleChargeCodeAndLaborCategory(chargeCodeId, txnType, chargeCodesToExclude, chargeCodeTypesToInclude);
          this.loading = false;
        });
      } else {
        forkJoin(
          this.ccDataService.getChargeCodeForUserAsDropdown(txnType, authorizedFrom, authorizedThru, this.chargeCodeDisplayProperties, userId),
          this.projectService.getProjectOnsiteWorkLocations()
        ).subscribe(res => {
          this.chargeCodeDropdown = res[0];
          this.projectWorkLocations = plainToClass(ProjectWorkLocation, res[1].list);
          this.handleChargeCodeAndLaborCategory(chargeCodeId, txnType, chargeCodesToExclude, chargeCodeTypesToInclude);
          this.loading = false;
        });
      }
    } else {
      console.warn("txnType and authorizedFrom must be specified in order to retrieve authorized charge code(s)!");
    }
  }

  /**
   * @desc exclude specified charge codes from the dropdown menu
   */
  handleChargeCodesToExclude(chargeCodesToExclude: Array<ProjectChargeCode | PurchaseChargeCode>) {
    if (chargeCodesToExclude) {
      chargeCodesToExclude.forEach(ccToExclude => {
        let index;
        if (ccToExclude instanceof PurchaseChargeCode) {
          index = this.chargeCodeDropdown.findIndex(cc => cc.value.id === ccToExclude.chargeCodeId);
        }
        console.log(`index = ${index}`);
        if (index >= 0) {
          console.log(`handleChargeCodeAndLaborCategory: removing cc = ${this.chargeCodeDropdown[index].value.chargeNumber}`);
          this.chargeCodeDropdown.splice(index, 1);
        }
      });
    }
  }

  handleChargeCodeTypesToInclude(chargeCodeTypesToInclude: ChargeCodeType[]) {
    if(chargeCodeTypesToInclude && chargeCodeTypesToInclude.length > 0){
      this.chargeCodeDropdown = this.chargeCodeDropdown.filter(cc => chargeCodeTypesToInclude.includes(cc.value.codeType));
    }

  }

  /**
   * @desc set selectedChargeCode based on the input charge code
   */
  handleChargeCodeAndLaborCategory(chargeCodeId: number, txnType: Transaction, chargeCodesToExclude: Array<ProjectChargeCode | PurchaseChargeCode>, chargeCodeTypesToInclude: ChargeCodeType[]) {

    // ToDo - this is where indirect charge codes can be filtered
    this.handleChargeCodesToExclude(chargeCodesToExclude);
    this.handleChargeCodeTypesToInclude(chargeCodeTypesToInclude);
    let cc = this.chargeCodeDropdown.find(cc => cc.value.id === chargeCodeId);
    if (cc) {
      this.selectedChargeCode = cc.value;
      this.handleChargeCode(this.selectedChargeCode, txnType);
      if (txnType == Transaction.Timesheet) {
        // this.handleLaborCategories(this.selectedChargeCode);
        // this.handleWorkLocation(this.selectedChargeCode, this.selectedLaborCategory);
      } else {
        this.selectedLaborCategory = null;
        this.laborCategoryDropdown = null;
        this.workLocationDropdown = null;
        this.chargeCodeFound = true;
        this.laborCategoryFound = true;
        this.workLocationFound = true;
        this.handler.emit(this.selectedChargeCode);
      }
    } else {
      this.chargeCodeFound = false;
      console.error("handleChargeCodeAndLaborCategory: charge code not found in the dropdown");
    }
  }

  onSelectChargeCode(selectedChargeCode: ProjectChargeCode, txnType: Transaction) {
    this.userResidenceId = null;
    this.clientLocationId = null;
    this.officeLocationId = null;
    this.laborCategoryId = null;
    this.selectedLaborCategory = null;
    this.selectedWorkLocation = null;
    this.chargeCodeFound = true;
    this.handleChargeCode(selectedChargeCode, txnType);
  }

  /**
   * @desc save the selected charge code and handle labor category by calling another method
   */
  handleChargeCode(selectedChargeCode: ProjectChargeCode, txnType: Transaction) {
    this.workLocationDropdown = [];

    if (!selectedChargeCode) {
      return;
    }

    if (this.chargeCode) {
      Object.assign(this.chargeCode, selectedChargeCode);
    }
    if (selectedChargeCode.codeType == ChargeCodeType.Unallowable
        || (selectedChargeCode.codeType == ChargeCodeType.Indirect && selectedChargeCode.payTypeCode == PayCodeType.Regular)) {
      if (selectedChargeCode.userAddressInfo) {
        if (selectedChargeCode.userAddressInfo.orgAddress) {
          this.workLocationDropdown.push({
            label: selectedChargeCode.userAddressInfo.orgAddress.addrName,
            value: selectedChargeCode.userAddressInfo.orgAddress
          });
        }
        if (selectedChargeCode.userAddressInfo.residentialAddress) {
          this.workLocationDropdown.push({
            label: selectedChargeCode.userAddressInfo.residentialAddress.addrLn1,
            value: selectedChargeCode.userAddressInfo.residentialAddress
          });
        }
      } else {
        console.error("Cannot get userAddressInfo in the charge code.");
      }
      if ( this.projectWorkLocations) {
        // For Regular paytype and Indirect, add project work locations
        let projectLocationDropDowns = this.transform.convertToDropDown("projectAddressName", this.projectWorkLocations);
        projectLocationDropDowns.forEach(wl => {
          this.workLocationDropdown.push(wl);
        });
      }
    }
    if (txnType == Transaction.Timesheet) {
      this.handleLaborCategories(selectedChargeCode);
      this.setDefaultWorkLocation(selectedChargeCode, this.selectedLaborCategory);
      this.handleWorkLocation(this.selectedChargeCode, this.selectedLaborCategory);
      this.handler.emit(selectedChargeCode);
    } else {
      this.selectedLaborCategory = null;
      this.laborCategoryDropdown = null;
      this.workLocationDropdown = null;
      this.selectedWorkLocation = null;
      this.chargeCodeFound = true;
      this.laborCategoryFound = true;
      this.handler.emit(selectedChargeCode);
    }
  }

  /**
   * @desc build a labor category dropdown menu based on the selected charge code
   * @param selectedChargeCode - selected change code
   */
  handleLaborCategories(selectedChargeCode: ProjectChargeCode) {
    let laborCategories: LaborCategoryInfo[];

    if (selectedChargeCode) {
      laborCategories = selectedChargeCode.laborCategoryInfo;
    }

    if (laborCategories && laborCategories.length >= 1) {
      this.laborCategoryDropdown = this.transform.convertToLongLabelDropDown(this.laborCategoryDisplayProperties, laborCategories);

      if (this.laborCategoryId) {
        let lc = this.laborCategoryDropdown.find(lc => lc.value.laborCategoryId === this.laborCategoryId);
        if (lc) {
          // input charge code and labor category found
          this.selectedLaborCategory = lc.value;
          this.onSelectLaborCategory(this.selectedLaborCategory);
          this.laborCategoryFound = true;
        } else {
          // labor category not found
          this.laborCategoryFound = false;
        }
      } else {
        if (laborCategories.length == 1) {
          // if there is only one labor category, automatically select it for the user
          this.selectedLaborCategory = this.laborCategoryDropdown[0].value;
          this.onSelectLaborCategory(this.selectedLaborCategory);
          this.laborCategoryFound = true;
        }
      }
    } else {
      // console.log("handleLaborCategories: no laborCategoryInfo to select");
      this.selectedLaborCategory = null;
      this.laborCategoryDropdown = null;
      this.chargeCodeFound = true;
      this.laborCategoryFound = true;
      if (((this.selectedChargeCode.codeType == ChargeCodeType.DirectProject || this.selectedChargeCode.codeType == ChargeCodeType.IndirectProject))) { //Only if it is a project chargecode and there is no labor category , then emit
        this.selectedChargeCode.laborCategoryCode = null;
        this.selectedChargeCode.laborCategoryId = null;
        this.handler.emit(selectedChargeCode);
      }
    }
  }

  // Fixme: Will the Id's collide?
  handleWorkLocation(selectedChargeCode: ProjectChargeCode, selectedLaborCategory?: LaborCategoryInfo) {
    if (selectedChargeCode) {
      if (selectedChargeCode.codeType == ChargeCodeType.DirectProject || selectedChargeCode.codeType == ChargeCodeType.IndirectProject) {
        if (selectedLaborCategory) {
          if (selectedLaborCategory.workLocationFromHome && this.userResidenceId) {
            let userResidence = this.workLocationDropdown.find(wl => wl.value.id === this.userResidenceId);
            if (userResidence) {
              this.workLocationFound = true;
              this.selectedWorkLocation = userResidence.value;
              this.onSelectWorkLocation(this.selectedWorkLocation);
            } else {
              this.workLocationFound = false;
            }
          } else {
            if (this.officeLocationId) {
              this.workLocationFound = true;
              let officeLocation = this.workLocationDropdown.find(wl => wl.value.officeLocationId == this.officeLocationId);
              if (officeLocation) {
                this.selectedWorkLocation = officeLocation.value;
                this.onSelectWorkLocation(this.selectedWorkLocation);
              } else {
                this.workLocationFound = false;
              }
            } else if (this.clientLocationId) {
              let clientLocation = this.workLocationDropdown.find(wl => wl.value.clientLocationId == this.clientLocationId);
              if (clientLocation) {
                this.workLocationFound = true;
                this.selectedWorkLocation = clientLocation.value;
                this.onSelectWorkLocation(this.selectedWorkLocation);
              } else {
                this.workLocationFound = false;
              }
            }
          }

        }
      } else if (this.selectedChargeCode.codeType == ChargeCodeType.Unallowable
        || (this.selectedChargeCode.codeType == ChargeCodeType.Indirect && this.selectedChargeCode.payTypeCode == PayCodeType.Regular)) {
        if(this.clientLocationId){
          let clientLocation = this.workLocationDropdown.find(wl => wl.value.clientLocationId === this.clientLocationId);
          if (clientLocation) {
            this.workLocationFound = true;
            this.selectedWorkLocation = clientLocation.value;
            this.onSelectWorkLocation(this.selectedWorkLocation);
          } else {
            this.workLocationFound = false;
          }
        } else if (this.userResidenceId) {
          let userResidence = this.workLocationDropdown.find(wl => wl.value.id === this.userResidenceId);
          if (userResidence) {
            this.workLocationFound = true;
            this.selectedWorkLocation = userResidence.value;
            this.onSelectWorkLocation(this.selectedWorkLocation);
          } else {
            this.workLocationFound = false;
          }
        } else if (this.officeLocationId) {
          let officeLocation = this.workLocationDropdown.find(wl => wl.value.id === this.officeLocationId);
          if (officeLocation) {
            this.workLocationFound = true;
            this.selectedWorkLocation = officeLocation.value;
            this.onSelectWorkLocation(this.selectedWorkLocation);
          } else {
            this.workLocationFound = false;
          }
        }
      } else if(this.selectedChargeCode.codeType == ChargeCodeType.Indirect){
        this.selectedChargeCode.userResidenceId = null;
        this.selectedChargeCode.clientLocationId = null;
        this.selectedChargeCode.officeLocationId = null;
        this.selectedChargeCode.laborCategoryCode = null;
        this.selectedChargeCode.laborCategoryId = null;
        this.selectedChargeCode.workLocationName = null;
        this.workLocationFound = true;
        this.handler.emit(this.selectedChargeCode);
      }
    }

  }

  setDefaultWorkLocation(selectedChargeCode: ProjectChargeCode, selectedLaborCategory?: LaborCategoryInfo) {
    if (selectedChargeCode) {
      if (selectedLaborCategory && selectedLaborCategory.workLocationFromHome) {
        this.selectedWorkLocation = selectedChargeCode.userAddressInfo.residentialAddress;
        this.onSelectWorkLocation(this.selectedWorkLocation);
        this.workLocationFound = true;
      } else if (selectedChargeCode.userAddressInfo) {
        //if defaultWorkLocation is OfficeLocation in payroll payee settings (HR data manager screen), then default timesheet
        // work location to org address
        if (selectedChargeCode.defaultWorkLocation == WorkLocationType.OfficeLocation) {
          this.workLocationFound = true;
          this.selectedWorkLocation = selectedChargeCode.userAddressInfo.orgAddress;
          this.onSelectWorkLocation(this.selectedWorkLocation);
        } else if (selectedChargeCode.defaultWorkLocation == WorkLocationType.ClientLocation) {
          //if defaultWorkLocation is Client location in payroll payee settings (HR data manager screen), then default timesheet
          // work location to a client location
          let clientLocation = this.workLocationDropdown.find(wl => wl.value.clientLocationId != null);
          if (clientLocation) {
            this.workLocationFound = true;
            this.selectedWorkLocation = clientLocation.value;
            this.onSelectWorkLocation(this.selectedWorkLocation);
          }
        }
      } else {
        console.log("Cannot Set default work location");
      }
    }
  }

  /**
   * @desc save selected labor category and emit it
   * @param selectedLaborCategory - selected labor category
   */
  onSelectLaborCategory(selectedLaborCategory: LaborCategoryInfo) {
    // console.debug("onSelectLaborCategory: selectedLaborCategory =");
    // console.debug(selectedLaborCategory);
    if (selectedLaborCategory) {
      this.chargeCodeFound = true;
      this.laborCategoryFound = true;
      this.selectedChargeCode.laborCategoryId = selectedLaborCategory.laborCategoryId;
      this.selectedChargeCode.laborCategoryCode = selectedLaborCategory.laborCategoryCode;
      // this.handler.emit(this.selectedChargeCode);
      this.workLocationDropdown = [];
      console.log(this.selectedLaborCategory);
      if (this.selectedChargeCode.codeType == ChargeCodeType.DirectProject || this.selectedChargeCode.codeType == ChargeCodeType.IndirectProject) {
        if (this.selectedLaborCategory) {
          if (this.selectedLaborCategory.workLocationFromHome) {
            this.workLocationDropdown.push({
              label: this.selectedChargeCode.userAddressInfo.residentialAddress.addrLn1,
              value: this.selectedChargeCode.userAddressInfo.residentialAddress
            });
          } else if (this.selectedLaborCategory.projectWorkLocations != null) {
            // this.workLocationDropdown = this.transform.convertToDropDown("workLocationName", selectedLaborCategory.projectWorkLocations);
            for (let wl of selectedLaborCategory.projectWorkLocations) {
              this.workLocationDropdown.push({
                label: wl.clientLocationName ? wl.clientLocationName : wl.officeLocationName,
                value: wl
              });
            }
          }
        }

      }
      // else if (this.selectedChargeCode.codeType == ChargeCodeType.Indirect) {
      //   this.workLocationDropdown.push({ label: this.selectedChargeCode.userAddressInfo.orgAddress.addrName, value: this.selectedChargeCode.userAddressInfo.orgAddress.id });
      //   this.workLocationDropdown.push({ label: this.selectedChargeCode.userAddressInfo.residentialAddress.addrLn1, value: this.selectedChargeCode.userAddressInfo.residentialAddress.id });

      // }
    } else {
      this.selectedChargeCode.laborCategoryId = null;
      this.selectedChargeCode.laborCategoryCode = null;
    }
  }

  onSelectWorkLocation(selectedWorkLocation: any) {

    console.log(selectedWorkLocation);
    if (this.selectedChargeCode.codeType == ChargeCodeType.DirectProject || this.selectedChargeCode.codeType == ChargeCodeType.IndirectProject) {
      if (this.selectedLaborCategory) {
        if (this.selectedLaborCategory.workLocationFromHome) {
          this.selectedChargeCode.userResidenceId = selectedWorkLocation.id;
          this.selectedChargeCode.officeLocationId = null;
          this.selectedChargeCode.clientLocationId = null;
          this.selectedChargeCode.workLocationName = selectedWorkLocation.addrLn1;
        } else {
          this.selectedChargeCode.userResidenceId = null;
          this.selectedChargeCode.officeLocationId = selectedWorkLocation.officeLocationId;
          this.selectedChargeCode.clientLocationId = selectedWorkLocation.clientLocationId;
          this.selectedChargeCode.workLocationName = selectedWorkLocation.addrName;

        }
      }
    } else if ((this.selectedChargeCode.codeType == ChargeCodeType.Indirect && this.selectedChargeCode.payTypeCode == PayCodeType.Regular) || this.selectedChargeCode.codeType == ChargeCodeType.Unallowable) {
      // E.g: Holiday which is Indirect type with Paycode Holiday will not set the work location.
      // E.g: GnA charge code of type Indirect with paycode Regular (RT) will require work location
      //Check if the selectedWorkLocation is of Type UserAddress or OrgAddress and then set then values
      if (selectedWorkLocation.timesheetWorkLocationType == TimesheetWorkLocationType.UserResidence) {
        this.selectedChargeCode.userResidenceId = selectedWorkLocation.id;
        this.selectedChargeCode.clientLocationId = null;
        this.selectedChargeCode.officeLocationId = null;
        this.selectedChargeCode.laborCategoryCode = null;
        this.selectedChargeCode.laborCategoryId = null;
        this.selectedChargeCode.workLocationName = selectedWorkLocation.addrLn1;

      } else if (selectedWorkLocation.timesheetWorkLocationType == TimesheetWorkLocationType.OfficeAddress) {
        this.selectedChargeCode.userResidenceId = null;
        this.selectedChargeCode.clientLocationId = null;
        this.selectedChargeCode.officeLocationId = selectedWorkLocation.id;
        this.selectedChargeCode.laborCategoryCode = null;
        this.selectedChargeCode.laborCategoryId = null;
        this.selectedChargeCode.workLocationName = selectedWorkLocation.addrName;
      } else if((this.selectedChargeCode.codeType == ChargeCodeType.Unallowable || this.selectedChargeCode.payTypeCode == PayCodeType.Regular) && selectedWorkLocation.clientLocationId) {
        this.selectedChargeCode.userResidenceId = null;
        this.selectedChargeCode.clientLocationId = selectedWorkLocation.clientLocationId;
        this.selectedChargeCode.officeLocationId = null;
        this.selectedChargeCode.laborCategoryCode = null;
        this.selectedChargeCode.laborCategoryId = null;
        this.selectedChargeCode.workLocationName = selectedWorkLocation.addrName;
      }
    }
    this.workLocationFound = true;
    this.handler.emit(this.selectedChargeCode);

  }
}
